import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import { getApiErrorMessage } from "@/utils/apiError";
import { useTranslation } from "react-i18next";
import { useAccounts } from "@/hooks/useAccounts";
import { useCategories, useCategoryColors } from "@/hooks/useCategories";
import { useInstallments } from "@/hooks/useInstallments";
import { useSavings } from "@/hooks/useSavings";
import { useSavingPockets } from "@/hooks/useSavingPockets";
import { useStandardPeriodRange } from "@/hooks/useStandardPeriodRange";
import { useTags } from "@/hooks/useTags";
import type { Transaction } from "@/hooks/useTransactions";
import {
  useAddTransaction,
  useBulkUpdateTransactions,
  useDeleteTransactions,
  useTransactionTypes,
  useTransactions,
  useUpdateTransaction,
} from "@/hooks/useTransactions";
import { BulkUpdateTransactionsModal, type BulkUpdateTransactionData } from "@/domains/transactions/components/BulkUpdateTransactionsModal";
import { NewTransactionModal } from "@/domains/transactions/components/NewTransactionModal";
import { localTodayYMD, parseDateLocal, toDateInputValue } from "@/utils/dateUtils";
import { formatCurrency, formatDate } from "@/utils/formatters";
import { calculateFinancialTotals } from "@/utils/financialTotals";
import type { StandardPeriodFilter } from "@/utils/dateUtils";
import { confirmAction } from "@/components/ui/confirmDialog";
import { AppButton } from "@/components/ui/primitives";
import { CardActionMenu } from "@/components/ui/CardActionMenu";
import { Chip, JerryNote } from "@/components/ds";
import { FiCheck } from "react-icons/fi";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { useNavigationState } from "@/hooks/useNavigationState";
import { usePreferencesStore } from "@/stores/usePreferencesStore";
import { DomainChatTrigger } from "@/domains/chat/components/DomainChatTrigger";
import { DomainSidebarPeriod } from "@/components/ui/DomainSidebar";
import { FlowSummary } from "@/domains/transactions/components/ds/FlowSummary";
import { TransactionsToolbar } from "@/domains/transactions/components/ds/TransactionsToolbar";
import { ActiveFilterChips } from "@/domains/transactions/components/ds/ActiveFilterChips";
import { MovementGroup, MovementRow, MovementTableHead } from "@/domains/transactions/components/ds/MovementTable";
import { TransactionDetailSheet, DetailFile } from "@/domains/transactions/components/detail/TransactionDetailSheet";
import { DeleteTransactionDialog } from "@/domains/transactions/components/detail/DeleteTransactionDialog";
import { UndoToast } from "@/domains/transactions/components/detail/UndoToast";
import { TransactionsState } from "@/domains/transactions/components/detail/TransactionsState";
import {
  TransactionActivityList,
  type TransactionActivityGroup,
} from "@/domains/transactions/components/TransactionActivityList";
import {
  TransactionFilterOptions,
  type TransactionActiveFilter,
  type TransactionFilterGroup,
} from "@/domains/transactions/components/TransactionFiltersPanel";
import {
  buildTransactionMutationPayload,
  buildTransactionPatchPayload,
  getTicketHref,
} from "@/domains/transactions/utils/transactionPayloads";
import "@/domains/transactions/transactions.css";

interface EnrichedTransaction {
  transaction: Transaction;
  accountName: string;
  categoryName?: string;
  tagName?: string;
  savingPlanName?: string;
  savingPocketLabel?: string;
  installmentName?: string;
  typeName: string;
  direction: "income" | "expense" | "transfer";
}

type DirectionFilter = "all" | EnrichedTransaction["direction"];
type TransactionCycleFilter = "all" | StandardPeriodFilter;
type TransactionSortMode = "date-desc" | "date-asc" | "amount-desc" | "amount-asc";
type OpenPopover = "cycle" | "filters" | "sort" | null;
// Un solo aviso a la vez: borrar (diferido, se confirma al cerrar el aviso) o crear (solo resalta y deshace).
type UndoToastState = { kind: "delete"; id: number } | { kind: "create"; id: number | null };
const SORT_MODES: TransactionSortMode[] = ["date-desc", "date-asc", "amount-desc", "amount-asc"];

export const TransactionsView = () => {
  const { t } = useTranslation(["common", "transactions"]);
  const isAdvancedMode = usePreferencesStore((state) => state.appMode === "advanced");
  const { data: accounts = [] } = useAccounts();
  const { data: categories = [] } = useCategories();
  const { data: tags = [] } = useTags();
  const { data: savings } = useSavings();
  const { data: savingPockets = [] } = useSavingPockets({ enabled: FEATURE_FLAGS.savingPockets });
  const { data: installments = [] } = useInstallments();
  const { data: transactionTypes = [] } = useTransactionTypes();
  const addTransactionMutation = useAddTransaction();
  const updateTransactionMutation = useUpdateTransaction();
  const deleteTransactionsMutation = useDeleteTransactions();
  const bulkUpdateTransactionsMutation = useBulkUpdateTransactions();

  const { selectedPeriod, setSelectedPeriod, customStart, setCustomStart, customEnd, setCustomEnd, selectedRange } =
    useStandardPeriodRange<TransactionCycleFilter>("all", "transactions");
  const [searchTerm, setSearchTerm] = useNavigationState("transactions.searchTerm", "");
  const [selectedAccountIds, setSelectedAccountIds] = useNavigationState<number[]>("transactions.accountIds", []);
  const [selectedCategoryIds, setSelectedCategoryIds] = useNavigationState<number[]>("transactions.categoryIds", []);
  const [selectedTagIds, setSelectedTagIds] = useNavigationState<number[]>("transactions.tagIds", []);
  const [selectedInstallmentIds, setSelectedInstallmentIds] = useNavigationState<number[]>("transactions.installmentIds", []);
  const [selectedSavingPlanIds, setSelectedSavingPlanIds] = useNavigationState<number[]>("transactions.savingPlanIds", []);
  const [selectedSavingPocketIds, setSelectedSavingPocketIds] = useNavigationState<number[]>("transactions.savingPocketIds", []);
  const [selectedDirection, setSelectedDirection] = useNavigationState<DirectionFilter>("transactions.direction", "all");
  const [sortMode, setSortMode] = useState<TransactionSortMode>("date-desc");
  const [openPopover, setOpenPopover] = useState<OpenPopover>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const sortMenuRef = useRef<HTMLDivElement | null>(null);
  const [detailTransaction, setDetailTransaction] = useState<Transaction | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<Transaction | null>(null);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
  const [undoToast, setUndoToast] = useState<UndoToastState | null>(null);
  const [undoToastKey, setUndoToastKey] = useState(0);
  const [createdTransactionId, setCreatedTransactionId] = useState<number | null>(null);
  const [isNewTransactionModalOpen, setIsNewTransactionModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<number[]>([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isBulkUpdateModalOpen, setIsBulkUpdateModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(8);
  const [swipedTransactionId, setSwipedTransactionId] = useState<number | null>(null);
  const gestureRef = useRef<{
    id: number | null;
    startX: number;
    startY: number;
    action: "tap" | "swipe";
  }>({
    id: null,
    startX: 0,
    startY: 0,
    action: "tap",
  });

  const cycleOptions = useMemo(
    () => [
      { id: "all" as const, label: t("common.filters.allCycles") },
      { id: "this-month" as const, label: t("common.periods.thisMonth") },
      { id: "last-month" as const, label: t("common.periods.lastMonth") },
      { id: "custom" as const, label: t("common.periods.custom") },
    ],
    [t],
  );

  const accountOptions = useMemo(
    () => [...accounts].sort((a, b) => a.name.localeCompare(b.name)),
    [accounts],
  );

  const categoryOptions = useMemo(
    () => [...categories].sort((a, b) => a.name.localeCompare(b.name)),
    [categories],
  );

  const tagOptions = useMemo(
    () => [...tags].sort((a, b) => a.name.localeCompare(b.name)),
    [tags],
  );

  const installmentOptions = useMemo(
    () => [...installments].sort((a, b) => a.name.localeCompare(b.name)),
    [installments],
  );

  const directionOptions = useMemo(
    () => [
      { id: "all" as const, label: t("transactions.filterAllTypes", { defaultValue: "All types" }) },
      { id: "income" as const, label: t("transactions.activityTypeInflow", { defaultValue: "Inflow" }) },
      { id: "expense" as const, label: t("transactions.activityTypeOutflow", { defaultValue: "Outflow" }) },
    ],
    [t],
  );

  const transactionQueryParams = useMemo(
    () => ({
      transaction_date: "desc",
      ...(selectedPeriod === "all"
        ? {}
        : {
            billable_date_gte: selectedRange.start,
            billable_date_lte: selectedRange.end,
          }),
    }),
    [selectedPeriod, selectedRange.end, selectedRange.start],
  );

  const { data: transactionsData, isError: isTransactionsError, refetch: refetchTransactions } = useTransactions(transactionQueryParams);
  const { data: categoryColors = [] } = useCategoryColors();
  // Pending deletes stay hidden until their undo toast closes; the server call happens then.
  const transactions = useMemo(
    () => (transactionsData ?? []).filter((transaction) => !pendingDeleteIds.includes(transaction.id)),
    [transactionsData, pendingDeleteIds],
  );
  const categoryColorHexById = useMemo(() => {
    const hexById = new Map(categoryColors.map((color) => [color.id, color.hex]));
    return new Map(categories.flatMap((category) => {
      const hex = category.color_id ? hexById.get(category.color_id) : undefined;
      return hex ? [[category.id, hex] as const] : [];
    }));
  }, [categories, categoryColors]);
  const savingPlans = useMemo(() => savings?.saving_plans_balance ?? [], [savings?.saving_plans_balance]);
  const savingPlanOptions = useMemo(
    () => [...savingPlans].sort((a, b) => a.name.localeCompare(b.name)),
    [savingPlans],
  );
  const savingPocketFilterOptions = useMemo(
    () => [...savingPockets].sort((a, b) => a.name.localeCompare(b.name)),
    [savingPockets],
  );

  const transactionTypeById = useMemo(() => new Map(transactionTypes.map((type) => [type.id, type.name])), [transactionTypes]);
  const accountById = useMemo(() => new Map(accounts.map((account) => [account.id, account.name])), [accounts]);
  const categoryById = useMemo(() => new Map(categories.map((category) => [category.id, category.name])), [categories]);
  const tagById = useMemo(() => new Map(tags.map((tag) => [tag.id, tag.name])), [tags]);
  const installmentById = useMemo(() => new Map(installments.map((installment) => [installment.id, installment.name])), [installments]);
  const savingPlanById = useMemo(() => new Map(savingPlans.map((plan) => [plan.id, plan.name])), [savingPlans]);
  const savingPocketLabelById = useMemo(() => new Map(savingPockets.map((pocket) => [pocket.id, pocket.name])), [savingPockets]);

  const enrichedTransactions = useMemo<EnrichedTransaction[]>(
    () =>
      transactions.map((transaction) => {
        const typeName = transactionTypeById.get(transaction.type_id) ?? "expense";
        const lowerType = typeName.toLowerCase();
        const direction: EnrichedTransaction["direction"] =
          lowerType.includes("deposit") || transaction.type_id === 1
            ? "income"
            : lowerType.includes("transfer") || transaction.type_id === 3
              ? "transfer"
              : "expense";

        return {
          transaction,
          accountName: accountById.get(transaction.account_id) ?? "Cuenta",
          categoryName: transaction.category_id ? categoryById.get(transaction.category_id) : undefined,
          tagName: transaction.tag_id ? tagById.get(transaction.tag_id) : undefined,
          savingPlanName: transaction.saving_plan_id ? savingPlanById.get(transaction.saving_plan_id) : undefined,
          savingPocketLabel: FEATURE_FLAGS.savingPockets && transaction.saving_pocket_id ? savingPocketLabelById.get(transaction.saving_pocket_id) : undefined,
          installmentName: transaction.installment_id ? installmentById.get(transaction.installment_id) : undefined,
          typeName,
          direction,
        };
      }),
    [transactions, transactionTypeById, accountById, categoryById, tagById, savingPlanById, savingPocketLabelById, installmentById],
  );

  const filteredTransactions = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase();

    return enrichedTransactions.filter((item) => {
      if (selectedDirection !== "all" && item.direction !== selectedDirection) return false;
      if (selectedAccountIds.length > 0 && !selectedAccountIds.includes(item.transaction.account_id)) return false;
      if (selectedCategoryIds.length > 0 && !selectedCategoryIds.includes(item.transaction.category_id ?? -1)) return false;
      if (selectedTagIds.length > 0 && !selectedTagIds.includes(item.transaction.tag_id ?? -1)) return false;
      if (selectedInstallmentIds.length > 0 && !selectedInstallmentIds.includes(item.transaction.installment_id ?? -1)) return false;
      if (selectedSavingPlanIds.length > 0 && !selectedSavingPlanIds.includes(item.transaction.saving_plan_id ?? -1)) return false;
      if (FEATURE_FLAGS.savingPockets && selectedSavingPocketIds.length > 0 && !selectedSavingPocketIds.includes(item.transaction.saving_pocket_id ?? -1)) return false;
      if (!query) return true;

      return (
        item.transaction.name.toLocaleLowerCase().includes(query) ||
        item.transaction.description?.toLocaleLowerCase().includes(query) ||
        item.accountName.toLocaleLowerCase().includes(query) ||
        item.categoryName?.toLocaleLowerCase().includes(query) ||
        item.tagName?.toLocaleLowerCase().includes(query) ||
        item.installmentName?.toLocaleLowerCase().includes(query) ||
        item.savingPlanName?.toLocaleLowerCase().includes(query) ||
        (FEATURE_FLAGS.savingPockets && item.savingPocketLabel?.toLocaleLowerCase().includes(query))
      );
    });
  }, [
    enrichedTransactions,
    searchTerm,
    selectedAccountIds,
    selectedCategoryIds,
    selectedInstallmentIds,
    selectedDirection,
    selectedSavingPlanIds,
    selectedSavingPocketIds,
    selectedTagIds,
  ]);

  const activeFilters = useMemo<TransactionActiveFilter[]>(() => {
    const filters: TransactionActiveFilter[] = [];

    const pushGroupFilter = (
      id: string,
      values: number[],
      options: { id: number; name: string }[],
      multiLabel: string,
      reset: () => void,
    ) => {
      if (values.length === 0) return;
      filters.push({
        id,
        label:
          values.length === 1
            ? options.find((option) => option.id === values[0])?.name ?? String(values[0])
            : multiLabel,
        remove: reset,
      });
    };

    if (selectedDirection !== "all") {
      filters.push({
        id: "direction",
        label: directionOptions.find((option) => option.id === selectedDirection)?.label ?? selectedDirection,
        remove: () => setSelectedDirection("all"),
      });
    }

    pushGroupFilter(
      "accounts",
      selectedAccountIds,
      accountOptions,
      t("transactions.multiAccountsSelected", { count: selectedAccountIds.length, defaultValue: "{{count}} accounts" }),
      () => setSelectedAccountIds([]),
    );
    pushGroupFilter(
      "categories",
      selectedCategoryIds,
      categoryOptions,
      t("transactions.multiCategoriesSelected", { count: selectedCategoryIds.length, defaultValue: "{{count}} categories" }),
      () => setSelectedCategoryIds([]),
    );
    pushGroupFilter(
      "tags",
      selectedTagIds,
      tagOptions,
      t("transactions.multiTagsSelected", { count: selectedTagIds.length, defaultValue: "{{count}} tags" }),
      () => setSelectedTagIds([]),
    );
    pushGroupFilter(
      "installments",
      selectedInstallmentIds,
      installmentOptions,
      t("transactions.multiInstallmentsSelected", { count: selectedInstallmentIds.length, defaultValue: "{{count}} installments" }),
      () => setSelectedInstallmentIds([]),
    );
    pushGroupFilter(
      "savingPlans",
      selectedSavingPlanIds,
      savingPlanOptions,
      t("transactions.multiSavingPlansSelected", { count: selectedSavingPlanIds.length, defaultValue: "{{count}} saving plans" }),
      () => setSelectedSavingPlanIds([]),
    );
    if (FEATURE_FLAGS.savingPockets) {
      pushGroupFilter(
        "savingPockets",
        selectedSavingPocketIds,
        savingPocketFilterOptions,
        t("transactions.multiSavingPocketsSelected", { count: selectedSavingPocketIds.length, defaultValue: "{{count}} saving pockets" }),
        () => setSelectedSavingPocketIds([]),
      );
    }

    return filters;
  }, [
    accountOptions,
    categoryOptions,
    installmentOptions,
    directionOptions,
    savingPlanOptions,
    savingPocketFilterOptions,
    selectedAccountIds,
    selectedCategoryIds,
    selectedInstallmentIds,
    selectedDirection,
    selectedSavingPlanIds,
    selectedSavingPocketIds,
    selectedTagIds,
    setSelectedAccountIds,
    setSelectedCategoryIds,
    setSelectedDirection,
    setSelectedInstallmentIds,
    setSelectedSavingPlanIds,
    setSelectedSavingPocketIds,
    setSelectedTagIds,
    t,
    tagOptions,
  ]);

  const filterGroups = useMemo<TransactionFilterGroup[]>(
    () => [
      {
        id: "accounts",
        label: t("common.labels.account"),
        emptyLabel: t("transactions.allAccounts", { defaultValue: "All accounts" }),
        options: accountOptions.map((account) => ({ id: account.id, label: account.name })),
        values: selectedAccountIds,
        clear: () => setSelectedAccountIds([]),
        toggle: (value: number) => toggleMultiFilter(value, setSelectedAccountIds),
      },
      {
        id: "categories",
        label: t("common.labels.category"),
        emptyLabel: t("transactions.allCategories", { defaultValue: "All categories" }),
        options: categoryOptions.map((category) => ({ id: category.id, label: category.name })),
        values: selectedCategoryIds,
        clear: () => setSelectedCategoryIds([]),
        toggle: (value: number) => toggleMultiFilter(value, setSelectedCategoryIds),
      },
      {
        id: "tags",
        label: t("common.labels.tag", { defaultValue: "Tag" }),
        emptyLabel: t("transactions.allTags", { defaultValue: "All tags" }),
        options: tagOptions.map((tag) => ({ id: tag.id, label: tag.name })),
        values: selectedTagIds,
        clear: () => setSelectedTagIds([]),
        toggle: (value: number) => toggleMultiFilter(value, setSelectedTagIds),
      },
      ...(isAdvancedMode ? [
        {
          id: "installments",
          label: t("common.labels.installment"),
          emptyLabel: t("transactions.allInstallments", { defaultValue: "All installments" }),
          options: installmentOptions.map((installment) => ({ id: installment.id, label: installment.name })),
          values: selectedInstallmentIds,
          clear: () => setSelectedInstallmentIds([]),
          toggle: (value: number) => toggleMultiFilter(value, setSelectedInstallmentIds),
        },
        {
          id: "savingPlans",
          label: t("common.labels.savings"),
          emptyLabel: t("transactions.allSavingPlans", { defaultValue: "All saving plans" }),
          options: savingPlanOptions.map((plan) => ({ id: plan.id, label: plan.name })),
          values: selectedSavingPlanIds,
          clear: () => setSelectedSavingPlanIds([]),
          toggle: (value: number) => toggleMultiFilter(value, setSelectedSavingPlanIds),
        },
        ...(FEATURE_FLAGS.savingPockets ? [{
          id: "savingPockets",
          label: t("common.createLabels.savingPockets"),
          emptyLabel: t("transactions.allSavingPockets", { defaultValue: "All saving pockets" }),
          options: savingPocketFilterOptions.map((pocket) => ({ id: pocket.id, label: pocket.name })),
          values: selectedSavingPocketIds,
          clear: () => setSelectedSavingPocketIds([]),
          toggle: (value: number) => toggleMultiFilter(value, setSelectedSavingPocketIds),
        }] : []),
      ] : []),
    ],
    [
      accountOptions,
      categoryOptions,
      installmentOptions,
      isAdvancedMode,
      savingPlanOptions,
      savingPocketFilterOptions,
      selectedAccountIds,
      selectedCategoryIds,
      selectedInstallmentIds,
      selectedSavingPlanIds,
      selectedSavingPocketIds,
      selectedTagIds,
      setSelectedAccountIds,
      setSelectedCategoryIds,
      setSelectedInstallmentIds,
      setSelectedSavingPlanIds,
      setSelectedSavingPocketIds,
      setSelectedTagIds,
      t,
      tagOptions,
    ],
  );

  const sortedTransactions = useMemo(() => {
    const dateValue = (item: EnrichedTransaction) => parseDateLocal(item.transaction.transaction_date).getTime();
    const amountValue = (item: EnrichedTransaction) => Math.abs(Number(item.transaction.amount));
    return [...filteredTransactions].sort((a, b) => {
      switch (sortMode) {
        case "date-asc":
          return dateValue(a) - dateValue(b) || a.transaction.id - b.transaction.id;
        case "amount-desc":
          return amountValue(b) - amountValue(a);
        case "amount-asc":
          return amountValue(a) - amountValue(b);
        default:
          return dateValue(b) - dateValue(a) || b.transaction.id - a.transaction.id;
      }
    });
  }, [filteredTransactions, sortMode]);

  const visibleTransactions = useMemo(
    () => sortedTransactions.slice(0, visibleCount),
    [sortedTransactions, visibleCount],
  );

  const groupedTransactions = useMemo<TransactionActivityGroup[]>(() => {
    const visibleIds = new Set(visibleTransactions.map((item) => item.transaction.id));
    const grouped = new Map<string, { key: string; label: string; items: EnrichedTransaction[] }>();

    sortedTransactions.forEach((item) => {
      const date = parseDateLocal(item.transaction.transaction_date);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const group = grouped.get(key);
      if (group) {
        group.items.push(item);
      } else {
        grouped.set(key, {
          key,
          label: formatDate(date, undefined, { weekday: "long", day: "numeric", month: "long" }),
          items: [item],
        });
      }
    });

    return [...grouped.values()].map((group) => ({
      ...group,
      total: calculateFinancialTotals(group.items.map((item) => item.transaction)).net,
      items: group.items.filter((item) => visibleIds.has(item.transaction.id)),
    })).filter((group) => group.items.length > 0);
  }, [sortedTransactions, visibleTransactions]);

  const highlightedTransactionId = useMemo(() => {
    const pool = visibleTransactions.filter((item) => item.direction !== "transfer");
    if (pool.length === 0) return null;
    const top = [...pool].sort((a, b) => Math.abs(b.transaction.amount) - Math.abs(a.transaction.amount))[0];
    return top?.transaction.id ?? null;
  }, [visibleTransactions]);

  const primaryInsight = useMemo(() => {
    const topIncome = filteredTransactions
      .filter((item) => item.direction === "income")
      .sort((a, b) => Number(b.transaction.amount || 0) - Number(a.transaction.amount || 0))[0];
    const topExpense = filteredTransactions
      .filter((item) => item.direction === "expense")
      .sort((a, b) => Number(b.transaction.amount || 0) - Number(a.transaction.amount || 0))[0];

    return {
      income: topIncome
        ? t("transactions.insightLargestIncome", {
            name: topIncome.transaction.name,
            amount: formatCurrency(topIncome.transaction.amount),
          })
        : t("transactions.insightNoIncome"),
      expense: topExpense
        ? t("transactions.insightLargestExpense", {
            name: topExpense.transaction.name,
            amount: formatCurrency(topExpense.transaction.amount),
          })
        : t("transactions.insightNoExpense"),
    };
  }, [filteredTransactions, t]);

  const heroTotals = useMemo(() => {
    const totals = calculateFinancialTotals(
      filteredTransactions.map((item) => item.transaction),
    );
    return { income: totals.income, outflow: totals.expense, net: totals.net };
  }, [filteredTransactions]);

  const getModalTypeName = (transaction: Transaction) => {
    const typeName = transactionTypeById.get(transaction.type_id)?.toLowerCase() ?? "expense";
    if (typeName.includes("deposit")) return "deposit";
    if (typeName.includes("transfer")) return "transfer";
    return "expense";
  };

  useEffect(() => {
    setVisibleCount(8);
    setSwipedTransactionId(null);
  }, [
    selectedPeriod,
    customStart,
    customEnd,
    searchTerm,
    selectedAccountIds,
    selectedCategoryIds,
    selectedInstallmentIds,
    selectedDirection,
    selectedSavingPlanIds,
    selectedTagIds,
    sortMode,
  ]);

  useEffect(() => {
    const availableIds = new Set(transactions.map((transaction) => transaction.id));
    setSelectedTransactionIds((prev) => {
      const next = prev.filter((id) => availableIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [transactions]);

  useEffect(() => {
    const filteredIds = new Set(filteredTransactions.map((item) => item.transaction.id));
    setSelectedTransactionIds((prev) => {
      const next = prev.filter((id) => filteredIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [filteredTransactions]);

  const clearStructuredFilters = () => {
    setSelectedDirection("all");
    setSelectedAccountIds([]);
    setSelectedCategoryIds([]);
    setSelectedInstallmentIds([]);
    setSelectedSavingPlanIds([]);
    setSelectedSavingPocketIds([]);
    setSelectedTagIds([]);
  };

  const toggleMultiFilter = (
    value: number,
    setter: React.Dispatch<React.SetStateAction<number[]>>,
  ) => {
    setter((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  };

  const handlePressStart = (event: ReactPointerEvent<HTMLButtonElement>, transactionId: number) => {
    if (isSelectionMode) return;

    gestureRef.current = {
      id: transactionId,
      startX: event.clientX,
      startY: event.clientY,
      action: "tap",
    };
  };

  const handlePressMove = (event: ReactPointerEvent<HTMLButtonElement>, transactionId: number) => {
    if (isSelectionMode) return;
    if (gestureRef.current.id !== transactionId) return;

    const deltaX = event.clientX - gestureRef.current.startX;
    const deltaY = event.clientY - gestureRef.current.startY;

    if (Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY)) {
      gestureRef.current.action = "swipe";

      if (deltaX < -36) {
        setSwipedTransactionId(transactionId);
      } else if (deltaX > 20 && swipedTransactionId === transactionId) {
        setSwipedTransactionId(null);
      }
    }
  };

  const handlePressEnd = () => {
    gestureRef.current.id = null;
  };

  const handleTransactionClick = (transaction: Transaction) => {
    if (isSelectionMode) {
      setSelectedTransactionIds((prev) =>
        prev.includes(transaction.id) ? prev.filter((id) => id !== transaction.id) : [...prev, transaction.id],
      );
      return;
    }

    if (gestureRef.current.action === "swipe") {
      gestureRef.current.action = "tap";
      return;
    }

    if (swipedTransactionId === transaction.id) {
      setSwipedTransactionId(null);
      return;
    }

    setDetailTransaction(transaction);
  };

  const handleTransactionActionToggle = (event: ReactMouseEvent<HTMLSpanElement>, transactionId: number) => {
    event.preventDefault();
    event.stopPropagation();
    gestureRef.current.action = "tap";
    gestureRef.current.id = null;
    setSwipedTransactionId((currentId) => (currentId === transactionId ? null : transactionId));
  };

  const handleEditTransactionFromList = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsNewTransactionModalOpen(true);
    setSwipedTransactionId(null);
  };

  const handleDuplicateTransactionFromList = async (transaction: Transaction) => {
    try {
      setError(null);
      await addTransactionMutation.mutateAsync(
        buildTransactionMutationPayload({
          account_id: transaction.account_id,
          destination_account_id: transaction.destination_account_id,
          amount: Number(transaction.amount),
          category_id: transaction.category_id ?? undefined,
          tag_id: transaction.tag_id,
          type_id: transaction.type_id,
          transaction_date: localTodayYMD(),
          name: transaction.name,
          description: transaction.description,
          saving_plan_id: transaction.saving_plan_id,
          installment_id: transaction.installment_id,
        }),
      );
      setSwipedTransactionId(null);
    } catch {
      setError("Failed to duplicate transaction");
    }
  };

  const handleSelectionModeToggle = () => {
    setIsSelectionMode((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedTransactionIds([]);
        setIsBulkUpdateModalOpen(false);
      }
      return next;
    });
    setSwipedTransactionId(null);
  };

  const handleClearSelection = () => {
    setSelectedTransactionIds([]);
    setIsSelectionMode(false);
    setIsBulkUpdateModalOpen(false);
  };

  const handleBulkDelete = async () => {
    if (selectedTransactionIds.length === 0) return;
    const confirmed = await confirmAction({
      title: t("transactions.deleteSelectedPrompt", { defaultValue: "Delete selected transactions?" }),
      body: t("common.prompts.cannotUndo", { defaultValue: "This action cannot be undone." }),
      confirmLabel: t("common.actions.delete", { defaultValue: "Delete" }),
      cancelLabel: t("common.actions.cancel", { defaultValue: "Cancel" }),
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteTransactionsMutation.mutateAsync(selectedTransactionIds);
      handleClearSelection();
    } catch {
      setError("Failed to delete transactions");
    }
  };

  const handleBulkDuplicate = async () => {
    if (selectedTransactionIds.length === 0) return;

    try {
      setError(null);
      const selectedTransactions = transactions.filter((transaction) => selectedTransactionIds.includes(transaction.id));

      await Promise.all(
        selectedTransactions.map((transaction) =>
          addTransactionMutation.mutateAsync(
            buildTransactionMutationPayload({
              account_id: transaction.account_id,
              destination_account_id: transaction.destination_account_id,
              amount: Number(transaction.amount),
              category_id: transaction.category_id ?? undefined,
              tag_id: transaction.tag_id,
              type_id: transaction.type_id,
              transaction_date: localTodayYMD(),
              name: transaction.name,
              description: transaction.description,
              saving_plan_id: transaction.saving_plan_id,
              installment_id: transaction.installment_id,
            }),
          ),
        ),
      );

      handleClearSelection();
    } catch {
      setError("Failed to duplicate transactions");
    }
  };

  const handleBulkUpdate = async (data: BulkUpdateTransactionData) => {
    await bulkUpdateTransactionsMutation.mutateAsync({
      id_list: selectedTransactionIds,
      ...data,
    });

    setError(null);
    setIsBulkUpdateModalOpen(false);
    handleClearSelection();
  };

  const handleModalClose = () => {
    setIsNewTransactionModalOpen(false);
    setSelectedTransaction(null);
    setError(null);
  };

  // Deletes are deferred: the row hides now and the API call runs when the undo toast closes.
  const commitPendingDelete = useCallback(async (id: number) => {
    try {
      await deleteTransactionsMutation.mutateAsync([id]);
      setPendingDeleteIds((prev) => prev.filter((pendingId) => pendingId !== id));
    } catch {
      setPendingDeleteIds((prev) => prev.filter((pendingId) => pendingId !== id));
      setError(t("transactions.deleteFailed"));
    }
  }, [deleteTransactionsMutation, t]);

  // Keep the latest pending ids reachable from the unmount flush below.
  const pendingDeleteIdsRef = useRef<number[]>([]);
  const commitRef = useRef(commitPendingDelete);
  useEffect(() => {
    pendingDeleteIdsRef.current = pendingDeleteIds;
    commitRef.current = commitPendingDelete;
  }, [pendingDeleteIds, commitPendingDelete]);
  useEffect(() => () => {
    // Leaving the view must not lose a delete the user already confirmed.
    pendingDeleteIdsRef.current.forEach((id) => void commitRef.current(id));
  }, []);

  const showUndoToast = (next: UndoToastState) => {
    if (undoToast?.kind === "delete") {
      void commitPendingDelete(undoToast.id);
    }
    setUndoToast(next);
    setUndoToastKey((key) => key + 1);
  };

  const confirmDeleteCandidate = () => {
    if (!deleteCandidate) return;
    const { id } = deleteCandidate;
    setDeleteCandidate(null);
    setDetailTransaction(null);
    setIsNewTransactionModalOpen(false);
    setSelectedTransaction(null);
    setSwipedTransactionId(null);
    setPendingDeleteIds((prev) => [...prev, id]);
    showUndoToast({ kind: "delete", id });
  };

  const undoPendingDelete = (id: number) => {
    setPendingDeleteIds((prev) => prev.filter((pendingId) => pendingId !== id));
    setUndoToast(null);
  };

  const undoCreatedTransaction = async (id: number) => {
    setUndoToast(null);
    setCreatedTransactionId(null);
    try {
      await deleteTransactionsMutation.mutateAsync([id]);
    } catch {
      setError(t("transactions.deleteFailed"));
    }
  };

  const dismissUndoToast = () => {
    if (undoToast?.kind === "delete") {
      void commitPendingDelete(undoToast.id);
    } else {
      setCreatedTransactionId(null);
    }
    setUndoToast(null);
  };

  const handleCreateTransaction = async (transactionData: {
    description?: string;
    name: string;
    amount: string;
    category: string;
    tag_id?: number | null;
    date: string;
    account: string;
    destination_account?: string;
    type: string;
    ticket_img?: string | null;
    saving_plan_id?: number | null;
    saving_pocket_id?: number | null;
    installment_id?: number | null;
  }) => {
    try {
      setError(null);
      const account = accounts.find((a) => a.name === transactionData.account);
      if (!account) throw new Error("Selected account not found");

      const category = transactionData.category ? categories.find((c) => c.name === transactionData.category) : undefined;
      const typeLower = transactionData.type.toLowerCase();
      const matchedType = transactionTypes.find((t) => t.name.toLowerCase() === typeLower);
      const typeId = matchedType?.id ?? (typeLower === "deposit" ? 1 : typeLower === "transfer" ? 3 : 2);
      const destinationAccount =
        typeLower === "transfer" && transactionData.destination_account
          ? accounts.find((a) => a.name === transactionData.destination_account)
          : undefined;

      const created = await addTransactionMutation.mutateAsync(
        buildTransactionMutationPayload({
          account_id: account.id,
          destination_account_id: destinationAccount?.id,
          amount: parseFloat(transactionData.amount),
          category_id: category?.id,
          tag_id: transactionData.tag_id ?? undefined,
          type_id: typeId,
          transaction_date: transactionData.date,
          name: transactionData.name,
          ticket_img: transactionData.ticket_img ?? undefined,
          description: transactionData.description,
          saving_plan_id: transactionData.saving_plan_id ?? undefined,
          saving_pocket_id: FEATURE_FLAGS.savingPockets ? (transactionData.saving_pocket_id ?? undefined) : undefined,
          installment_id: transactionData.installment_id ?? undefined,
        }),
      );
      handleModalClose();
      setCreatedTransactionId(created?.id ?? null);
      showUndoToast({ kind: "create", id: created?.id ?? null });
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to create transaction"));
      throw err;
    }
  };

  const handleUpdateTransaction = async (transactionData: {
    description?: string;
    name: string;
    amount: string;
    category: string;
    tag_id?: number | null;
    date: string;
    account: string;
    destination_account?: string;
    type: string;
    ticket_img?: string | null;
    saving_plan_id?: number | null;
    saving_pocket_id?: number | null;
    installment_id?: number | null;
  }) => {
    try {
      setError(null);
      if (!selectedTransaction) throw new Error("No transaction selected");

      const account = accounts.find((a) => a.name === transactionData.account);
      if (!account) throw new Error("Selected account not found");

      const category = transactionData.category ? categories.find((c) => c.name === transactionData.category) : undefined;
      const typeLower = transactionData.type.toLowerCase();
      const matchedType = transactionTypes.find((t) => t.name.toLowerCase() === typeLower);
      const typeId = matchedType?.id ?? (typeLower === "deposit" ? 1 : typeLower === "transfer" ? 3 : 2);
      const destinationAccount =
        typeLower === "transfer" && transactionData.destination_account
          ? accounts.find((a) => a.name === transactionData.destination_account)
          : undefined;

      const nextAmount = parseFloat(transactionData.amount);
      const currentTicket = selectedTransaction.ticket_img?.trim() || null;
      const requestedTicket =
        transactionData.ticket_img === undefined
          ? undefined
          : transactionData.ticket_img === null
            ? null
            : transactionData.ticket_img.trim() || null;

      const payload = buildTransactionPatchPayload({
        account_id: selectedTransaction.account_id !== account.id ? account.id : undefined,
        destination_account_id:
          (selectedTransaction.destination_account_id ?? null) !== (destinationAccount?.id ?? null)
            ? (destinationAccount?.id ?? null)
            : undefined,
        amount: selectedTransaction.amount !== nextAmount ? nextAmount : undefined,
        category_id:
          (selectedTransaction.category_id ?? null) !== (category?.id ?? null) ? (category?.id ?? null) : undefined,
        tag_id:
          (selectedTransaction.tag_id ?? null) !== (transactionData.tag_id ?? null)
            ? (transactionData.tag_id ?? null)
            : undefined,
        type_id: selectedTransaction.type_id !== typeId ? typeId : undefined,
        transaction_date:
          toDateInputValue(selectedTransaction.transaction_date) !== transactionData.date
            ? transactionData.date
            : undefined,
        name: selectedTransaction.name.trim() !== transactionData.name.trim() ? transactionData.name.trim() : undefined,
        ticket_img:
          requestedTicket === undefined
            ? undefined
            : currentTicket !== requestedTicket
              ? requestedTicket
              : undefined,
        description:
          (selectedTransaction.description?.trim() ?? "") !== (transactionData.description?.trim() ?? "")
            ? transactionData.description?.trim() || null
            : undefined,
        saving_plan_id:
          (selectedTransaction.saving_plan_id ?? null) !== (transactionData.saving_plan_id ?? null)
            ? (transactionData.saving_plan_id ?? null)
            : undefined,
        saving_pocket_id: FEATURE_FLAGS.savingPockets ? (
          (selectedTransaction.saving_pocket_id ?? null) !== (transactionData.saving_pocket_id ?? null)
            ? (transactionData.saving_pocket_id ?? null)
            : undefined
        ) : undefined,
        installment_id:
          (selectedTransaction.installment_id ?? null) !== (transactionData.installment_id ?? null)
            ? (transactionData.installment_id ?? null)
            : undefined,
      });

      if (Object.keys(payload).length > 0) {
        await updateTransactionMutation.mutateAsync({ id: selectedTransaction.id, data: payload });
      }
      handleModalClose();
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to update transaction"));
      throw err;
    }
  };

  const handleModalSubmit = async (transactionData: {
    description?: string;
    name: string;
    amount: string;
    category: string;
    tag_id?: number | null;
    date: string;
    account: string;
    destination_account?: string;
    type: string;
    ticket_img?: string | null;
    saving_plan_id?: number | null;
    saving_pocket_id?: number | null;
    installment_id?: number | null;
  }) => {
    if (selectedTransaction) {
      await handleUpdateTransaction(transactionData);
    } else {
      await handleCreateTransaction(transactionData);
    }
  };

  const handleDelete = (transactionId: number) => {
    const transaction = transactionsData?.find((item) => item.id === transactionId);
    if (transaction) setDeleteCandidate(transaction);
    return Promise.resolve();
  };

  const closeOpenPopover = () => setOpenPopover(null);
  const togglePopover = (popover: Exclude<OpenPopover, null>) => {
    setOpenPopover((current) => (current === popover ? null : popover));
  };
  const closeSortMenu = () => {
    closeOpenPopover();
    toolbarRef.current?.querySelector<HTMLButtonElement>('[aria-controls="transactions-sort-menu"]')?.focus();
  };

  useEffect(() => {
    if (openPopover === "sort") {
      sortMenuRef.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
    }
  }, [openPopover]);

  // Escape and clicks outside the toolbar close the open popover.
  useEffect(() => {
    if (!openPopover) return undefined;
    const handlePointerDown = (event: PointerEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(event.target as Node)) closeOpenPopover();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeOpenPopover();
        if (openPopover === "sort") {
          toolbarRef.current?.querySelector<HTMLButtonElement>('[aria-controls="transactions-sort-menu"]')?.focus();
        }
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openPopover]);

  const bulkSelectionBarVisible = isSelectionMode && selectedTransactionIds.length > 0;
  const sortLabels: Record<TransactionSortMode, string> = {
    "date-desc": t("transactions.sortDateDesc"),
    "date-asc": t("transactions.sortDateAsc"),
    "amount-desc": t("transactions.sortAmountDesc"),
    "amount-asc": t("transactions.sortAmountAsc"),
  };
  const flowSpoken: Record<EnrichedTransaction["direction"], string> = {
    income: t("transactions.activityTypeInflow"),
    expense: t("transactions.activityTypeOutflow"),
    transfer: t("transactions.activityTypeTransfer"),
  };
  const cycleText = cycleOptions.find((option) => option.id === selectedPeriod)?.label ?? t("common.labels.cycle");
  const isEmptyLedger =
    transactionsData !== undefined &&
    transactions.length === 0 &&
    activeFilters.length === 0 &&
    !searchTerm.trim() &&
    selectedPeriod === "all";

  const detailItem = detailTransaction
    ? enrichedTransactions.find((item) => item.transaction.id === detailTransaction.id)
    : undefined;
  const detailCategoryHex = detailItem?.transaction.category_id
    ? categoryColorHexById.get(detailItem.transaction.category_id)
    : undefined;
  const detailTicketHref = detailItem ? getTicketHref(detailItem.transaction.ticket_img) : undefined;
  const detailFacts = detailItem
    ? [
        { label: t("common.labels.account"), value: detailItem.accountName },
        {
          label: t("common.labels.category"),
          value: detailItem.categoryName ? (
            <Chip variant={detailCategoryHex ? "filled" : "outline"} color={detailCategoryHex}>{detailItem.categoryName}</Chip>
          ) : "—",
        },
        { label: t("common.labels.tag"), value: detailItem.tagName ? <Chip variant="outline">{detailItem.tagName}</Chip> : "—" },
        {
          label: t("transactions.transactionDate"),
          value: formatDate(parseDateLocal(detailItem.transaction.transaction_date), undefined, { day: "numeric", month: "long", year: "numeric" }),
        },
        ...(detailItem.savingPlanName ? [{ label: t("transactions.savingPlan"), value: detailItem.savingPlanName }] : []),
        ...(detailItem.savingPocketLabel ? [{ label: t("transactions.savingPocket"), value: detailItem.savingPocketLabel }] : []),
        ...(detailItem.installmentName ? [{ label: t("transactions.installment"), value: detailItem.installmentName }] : []),
        ...(detailItem.transaction.description?.trim() ? [{ label: t("transactions.noteLabel"), value: detailItem.transaction.description.trim() }] : []),
        ...(detailTicketHref ? [{ label: t("transactions.receiptLabel"), value: <DetailFile href={detailTicketHref}>{t("transactions.ticket")}</DetailFile> }] : []),
      ]
    : [];

  const deleteCandidateItem = deleteCandidate
    ? enrichedTransactions.find((item) => item.transaction.id === deleteCandidate.id)
    : undefined;
  const deleteBalanceText = deleteCandidateItem
    ? deleteCandidateItem.direction === "transfer"
      ? t("transactions.deleteBalanceTransfer")
      : t(deleteCandidateItem.direction === "expense" ? "transactions.deleteBalanceUp" : "transactions.deleteBalanceDown", {
          account: deleteCandidateItem.accountName,
          amount: formatCurrency(Math.abs(Number(deleteCandidate?.amount ?? 0))),
        })
    : "";

  const undoToastAction = !undoToast
    ? undefined
    : undoToast.kind === "delete"
      ? () => undoPendingDelete(undoToast.id)
      : undoToast.id !== null
        ? () => void undoCreatedTransaction(undoToast.id as number)
        : undefined;

  return (
    <div className="app-page transactions-page overflow-x-clip">
      <div className="app-container max-w-none overflow-x-clip">
        <div className="relative mx-auto flex w-full min-w-0 flex-col gap-6 overflow-x-clip">
          <header className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <h1 className="app-title m-0 text-[var(--app-text)]">{t("transactions.pageTitle")}</h1>
            <FlowSummary
              label={t("transactions.flowSummary")}
              net={heroTotals.net}
              income={heroTotals.income}
              expense={heroTotals.outflow}
              incomeLabel={t("transactions.incomeLabel")}
              expenseLabel={t("transactions.expenseLabel")}
            />
          </header>

          <div ref={toolbarRef} className="relative md:sticky md:top-[104px] md:z-30">
            <div className="overflow-x-auto md:overflow-visible">
              <div className="min-w-[36rem] md:min-w-0">
                <TransactionsToolbar
                  ariaLabel={t("transactions.toolbarLabel")}
                  search={searchTerm}
                  onSearchChange={setSearchTerm}
                  searchPlaceholder={t("common.actions.search")}
                  cycle={{
                    text: cycleText,
                    label: t("common.labels.cycle"),
                    expanded: openPopover === "cycle",
                    onClick: () => togglePopover("cycle"),
                  }}
                  filters={{
                    text: t("transactions.filterToggle"),
                    label: t("transactions.filterToggle"),
                    expanded: openPopover === "filters",
                    count: activeFilters.length,
                    onClick: () => togglePopover("filters"),
                  }}
                  sort={{
                    text: sortLabels[sortMode],
                    label: `${t("transactions.sortLabel")}: ${sortLabels[sortMode]}`,
                    expanded: openPopover === "sort",
                    onClick: () => togglePopover("sort"),
                  }}
                />
              </div>
            </div>

            {openPopover === "sort" ? (
              <div
                ref={sortMenuRef}
                id="transactions-sort-menu"
                role="menu"
                aria-label={t("transactions.sortLabel")}
                className="app-reveal absolute right-0 top-full z-30 mt-2 w-[236px] max-w-full rounded-[var(--ds-radius-md)] border-[1.5px] border-[var(--ds-line)] bg-[var(--ds-surface-raised)] p-2 shadow-[var(--ds-shadow-3)]"
                onKeyDown={(event) => {
                  const items = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]')];
                  const index = items.indexOf(document.activeElement as HTMLButtonElement);
                  const next = event.key === "ArrowDown" ? (index + 1) % items.length
                    : event.key === "ArrowUp" ? (index - 1 + items.length) % items.length
                    : event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 : -1;
                  if (next >= 0) {
                    event.preventDefault();
                    items[next]?.focus();
                  } else if (event.key === "Tab") {
                    closeSortMenu();
                  }
                }}
              >
                {SORT_MODES.map((mode) => (
                  <AppButton
                    key={mode}
                    type="button"
                    role="menuitemradio"
                    aria-checked={sortMode === mode}
                    tabIndex={-1}
                    tone="ghost"
                    onClick={() => {
                      setSortMode(mode);
                      closeSortMenu();
                    }}
                    className="min-h-11 w-full justify-between rounded-[var(--ds-radius-sm)] px-3 text-left hover:bg-[var(--ds-line)]"
                  >
                    {sortLabels[mode]}
                    {sortMode === mode ? <FiCheck aria-hidden="true" /> : null}
                  </AppButton>
                ))}
              </div>
            ) : null}

            {openPopover === "cycle" ? (
              <div className="app-reveal absolute right-0 top-full z-30 mt-2 w-[360px] max-w-full rounded-[var(--ds-radius-lg)] bg-[var(--app-surface)] p-4 shadow-[var(--ds-shadow-3)]">
                <DomainSidebarPeriod
                  label={t("common.labels.cycle")}
                  selectedPeriod={selectedPeriod}
                  onPeriodChange={setSelectedPeriod}
                  options={cycleOptions}
                  customStart={customStart}
                  onCustomStartChange={setCustomStart}
                  customEnd={customEnd}
                  onCustomEndChange={setCustomEnd}
                  startDateLabel={t("common.labels.startDate")}
                  endDateLabel={t("common.labels.endDate")}
                />
              </div>
            ) : null}

            {openPopover === "filters" ? (
              <div
                role="dialog"
                aria-label={t("transactions.filtersLabel")}
                className="app-reveal absolute right-0 top-full z-30 mt-2 w-[360px] max-w-full rounded-[var(--ds-radius-lg)] bg-[var(--app-surface)] p-4 shadow-[var(--ds-shadow-3)]"
              >
                <TransactionFilterOptions
                  t={t}
                  selectedDirection={selectedDirection}
                  setSelectedDirection={setSelectedDirection}
                  directionOptions={directionOptions}
                  filterGroups={filterGroups}
                />
              </div>
            ) : null}
          </div>

          <ActiveFilterChips
            chips={activeFilters.map((filter) => ({
              id: filter.id,
              label: filter.label,
              onRemove: filter.remove,
              removeLabel: t("transactions.removeFilter", { label: filter.label, defaultValue: "Remove {{label}}" }),
            }))}
            countLabel={t("transactions.countLabel", { count: sortedTransactions.length })}
            clearLabel={activeFilters.length > 0 ? t("transactions.clearAllFilters") : undefined}
            onClear={activeFilters.length > 0 ? clearStructuredFilters : undefined}
          />

          <JerryNote ariaLabel={t("transactions.jerryInsightLabel")} trailing={<DomainChatTrigger domain="transactions" />}>
            {primaryInsight.expense} · {primaryInsight.income}
          </JerryNote>

          {!isSelectionMode ? (
            <div className="flex justify-end">
              <AppButton type="button" onClick={handleSelectionModeToggle} className="app-btn-raised flex-none">
                {t("common.actions.select")}
              </AppButton>
            </div>
          ) : null}

          {isSelectionMode ? (
            <div className="app-reveal sticky top-2 z-20 rounded-[var(--ds-radius-lg)] bg-[var(--app-panel-inset-bg)] px-3 py-3 backdrop-blur">
              <div className="flex items-center justify-between gap-3">
                <p className="min-w-0 text-xs font-medium text-[var(--app-text-secondary)]">
                  {selectedTransactionIds.length > 0
                    ? t("transactions.selectedCount", { count: selectedTransactionIds.length, defaultValue: "{{count}} selected" })
                    : t("transactions.tapToSelect", { defaultValue: "Tap movements to select" })}
                </p>
                <AppButton
                  type="button"
                  onClick={handleClearSelection}
                  tone="ghost"
                  size="sm"
                  shape="pill"
                  className="flex-none"
                >
                  {t("common.actions.cancel")}
                </AppButton>
              </div>
            </div>
          ) : null}

          {isTransactionsError && transactionsData === undefined ? (
            <TransactionsState
              kind="error"
              title={t("transactions.loadErrorTitle")}
              body={t("transactions.loadErrorBody")}
              primary={{ label: t("transactions.retry"), onClick: () => void refetchTransactions() }}
            />
          ) : isEmptyLedger ? (
            <TransactionsState
              kind="empty"
              title={t("transactions.emptyTitle")}
              body={t("transactions.emptyBody")}
              primary={{ label: t("transactions.recordMovement"), onClick: () => setIsNewTransactionModalOpen(true) }}
            />
          ) : (
            <>
              <div className={sortedTransactions.length === 0 ? undefined : "md:hidden"}>
                <TransactionActivityList
                  filteredCount={sortedTransactions.length}
                  groups={groupedTransactions}
                  highlightedTransactionId={highlightedTransactionId}
                  selectedTransactionIds={selectedTransactionIds}
                  isSelectionMode={isSelectionMode}
                  swipedTransactionId={swipedTransactionId}
                  isDuplicating={addTransactionMutation.isPending}
                  visibleCount={visibleCount}
                  onTransactionClick={handleTransactionClick}
                  onPressStart={handlePressStart}
                  onPressMove={handlePressMove}
                  onPressEnd={handlePressEnd}
                  onActionToggle={handleTransactionActionToggle}
                  onDuplicate={handleDuplicateTransactionFromList}
                  onEdit={handleEditTransactionFromList}
                  onDelete={handleDelete}
                  onCloseActions={() => setSwipedTransactionId(null)}
                  onViewMore={() => setVisibleCount((count) => count + 10)}
                />
              </div>

              {sortedTransactions.length > 0 ? (
                <div className="hidden md:block">
                  <MovementTableHead
                    labels={{
                      movement: t("transactions.tableMovement"),
                      category: t("common.labels.category"),
                      tag: t("common.labels.tag"),
                      account: t("common.labels.account"),
                      amount: t("transactions.tableAmount"),
                      actions: t("transactions.tableActions"),
                    }}
                  />
                  {groupedTransactions.map((group) => (
                    <MovementGroup key={group.key} label={group.label} total={group.total}>
                      {group.items.map((item) => {
                        const { transaction } = item;
                        const categoryHex = transaction.category_id ? categoryColorHexById.get(transaction.category_id) : undefined;
                        return (
                          <MovementRow
                            key={transaction.id}
                            id={String(transaction.id)}
                            name={transaction.name}
                            meta={transaction.description?.trim() || undefined}
                            category={item.categoryName ? { label: item.categoryName, colorVar: categoryHex } : undefined}
                            tag={item.tagName}
                            account={item.accountName}
                            amount={Math.abs(Number(transaction.amount))}
                            flow={item.direction}
                            highlighted={(createdTransactionId ?? highlightedTransactionId) === transaction.id}
                            selected={selectedTransactionIds.includes(transaction.id)}
                            selectable={isSelectionMode}
                            onClick={() => handleTransactionClick(transaction)}
                            actions={
                              !isSelectionMode ? <CardActionMenu
                                name={transaction.name}
                                menuLabel={`${t("transactions.tableActions")} ${transaction.name}`}
                                triggerClassName="text-[var(--ds-ink)]"
                                actions={[
                                  { kind: "edit", label: t("common.actions.edit"), onClick: () => handleEditTransactionFromList(transaction) },
                                  { kind: "duplicate", label: t("common.actions.duplicate"), disabled: addTransactionMutation.isPending, onClick: () => handleDuplicateTransactionFromList(transaction) },
                                  { kind: "delete", label: t("common.actions.delete"), onClick: () => setDeleteCandidate(transaction) },
                                ]}
                              /> : undefined
                            }
                            flowSpoken={flowSpoken}
                          />
                        );
                      })}
                    </MovementGroup>
                  ))}
                  {sortedTransactions.length > visibleCount ? (
                    <div className="mt-3 flex justify-end pb-8">
                      <button type="button" onClick={() => setVisibleCount((count) => count + 10)} className="app-focus-ring inline-flex min-h-11 items-center rounded-full px-4 text-sm font-medium text-[var(--app-text)] underline underline-offset-4 hover:bg-[var(--app-row-hover-bg)]">
                        {t("transactions.viewMore")} <span aria-hidden="true" className="ml-1">›</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>

        <NewTransactionModal
          isOpen={isNewTransactionModalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          isEditMode={Boolean(selectedTransaction)}
          title={selectedTransaction ? t("transactions.editMovement") : t("transactions.newMovement")}
          submitLabel={selectedTransaction ? t("common.actions.save", { defaultValue: "Guardar" }) : t("transactions.createMovement")}
          onDelete={selectedTransaction ? () => setDeleteCandidate(selectedTransaction) : undefined}
          deleteLabel={t("common.actions.delete")}
          initialData={
            selectedTransaction
              ? {
                  name: selectedTransaction.name,
                  amount: String(selectedTransaction.amount),
                  category: selectedTransaction.category_id ? categoryById.get(selectedTransaction.category_id) || "" : "",
                  tag_id: selectedTransaction.tag_id ?? undefined,
                  date: selectedTransaction.transaction_date,
                  account:
                    accountById.get(selectedTransaction.account_id) ||
                    selectedTransaction.account ||
                    accounts.find((account) => account.id === selectedTransaction.account_id)?.name ||
                    "",
                  destination_account: selectedTransaction.destination_account_id
                    ? accountById.get(selectedTransaction.destination_account_id) ||
                      accounts.find((account) => account.id === selectedTransaction.destination_account_id)?.name ||
                      ""
                    : "",
                  type:
                    transactionTypes.find((type) => type.id === selectedTransaction.type_id)?.name ||
                    getModalTypeName(selectedTransaction),
                  description: selectedTransaction.description || "",
                  ticket_img: selectedTransaction.ticket_img ?? undefined,
                  saving_plan_id: selectedTransaction.saving_plan_id ?? undefined,
                  saving_pocket_id: FEATURE_FLAGS.savingPockets ? (selectedTransaction.saving_pocket_id ?? undefined) : undefined,
                  installment_id: selectedTransaction.installment_id ?? undefined,
                }
              : undefined
          }
        />

        <BulkUpdateTransactionsModal
          isOpen={isBulkUpdateModalOpen}
          selectedCount={selectedTransactionIds.length}
          accounts={accounts}
          categories={categories}
          tags={tags}
          installments={installments}
          savingPlans={savingPlans}
          savingPockets={FEATURE_FLAGS.savingPockets ? savingPockets : []}
          transactionTypes={transactionTypes}
          isSubmitting={bulkUpdateTransactionsMutation.isPending}
          onClose={() => setIsBulkUpdateModalOpen(false)}
          onSubmit={handleBulkUpdate}
        />

        {isSelectionMode && selectedTransactionIds.length > 0 ? (
          <div className="app-toast app-reveal bottom-24 rounded-[var(--ds-radius-lg)] bg-[var(--app-panel-inset-bg)] px-3 py-3 shadow-[var(--ds-shadow-3)] backdrop-blur sm:px-4">
            {/* Two actions per row in a fixed grid, so the pairs stay aligned instead of
                reflowing with the label lengths. The bar is capped at 32rem and the labels
                are verbs — the count beside them already names what they act on. */}
            <div className="flex flex-col gap-3">
              <p className="text-sm font-semibold text-[var(--app-text)]">
                {t("transactions.selectedCount", { count: selectedTransactionIds.length, defaultValue: "{{count}} selected" })}
              </p>

              <div className="grid grid-cols-2 gap-2">
              <AppButton
                type="button"
                onClick={() => setIsBulkUpdateModalOpen(true)}
                tone="primary"
                size="sm"
                shape="pill"
              >
                {t("transactions.updateSelected")}
              </AppButton>
              <AppButton
                type="button"
                onClick={handleBulkDuplicate}
                disabled={addTransactionMutation.isPending}
                size="sm"
                shape="pill"
              >
                {t("common.actions.duplicate")}
              </AppButton>
              <AppButton
                type="button"
                onClick={handleBulkDelete}
                tone="dangerSoft"
                size="sm"
                shape="pill"
              >
                {t("common.actions.delete")}
              </AppButton>
              <AppButton
                type="button"
                onClick={handleClearSelection}
                tone="ghost"
                size="sm"
                shape="pill"
              >
                {t("common.actions.cancel")}
              </AppButton>
              </div>
            </div>
          </div>
        ) : null}

        {detailItem ? (
          <TransactionDetailSheet
            open
            onClose={() => setDetailTransaction(null)}
            title={t("transactions.movementDetail")}
            closeLabel={t("common.actions.close")}
            hero={{
              label: flowSpoken[detailItem.direction],
              amount: Number(detailItem.transaction.amount),
              flow: detailItem.direction,
              name: detailItem.transaction.name,
            }}
            facts={detailFacts}
            factsLabel={t("transactions.detailFactsLabel")}
            editLabel={t("common.actions.edit")}
            deleteLabel={t("common.actions.delete")}
            onEdit={() => {
              setDetailTransaction(null);
              setSelectedTransaction(detailItem.transaction);
              setIsNewTransactionModalOpen(true);
            }}
            onDelete={() => setDeleteCandidate(detailItem.transaction)}
          />
        ) : null}

        {deleteCandidate ? (
          <DeleteTransactionDialog
            open
            title={t("transactions.deleteTitle")}
            text={deleteBalanceText}
            summary={{
              name: deleteCandidate.name,
              amount: Number(deleteCandidate.amount),
              flow: deleteCandidateItem?.direction ?? "expense",
              meta: deleteCandidateItem?.accountName,
            }}
            confirmLabel={t("common.actions.delete")}
            cancelLabel={t("common.actions.cancel")}
            onConfirm={confirmDeleteCandidate}
            onCancel={() => setDeleteCandidate(null)}
          />
        ) : null}

        <UndoToast
          key={undoToastKey}
          open={Boolean(undoToast) && !bulkSelectionBarVisible}
          message={undoToast?.kind === "delete" ? t("transactions.deletedToast") : t("transactions.createdToast")}
          actionLabel={undoToastAction ? t("transactions.undoAction") : undefined}
          onAction={undoToastAction}
          onDismiss={dismissUndoToast}
        />

        {error ? (
          <p className="mt-4 text-sm text-[var(--app-danger)]">{error}</p>
        ) : null}
      </div>
    </div>
  );
};
